import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatINR } from "@/lib/format";
import { Link } from "react-router-dom";
import { ShieldCheck, X, Clock, Trash2, Plus, Tag, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";

export const Admin = ({ onAuth }) => {
  const { user, loading } = useAuth();
  const [list, setList] = useState([]);
  const [tab, setTab] = useState("pending");
  const [rejectFor, setRejectFor] = useState(null);
  const [deleteFor, setDeleteFor] = useState(null);
  const [note, setNote] = useState("");

  const load = () => {
    api.get("/admin/properties", { params: { status: tab } })
      .then(({ data }) => setList(data))
      .catch(() => {});
  };

  useEffect(() => {
    if (user?.role === "admin") load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, tab]);

  if (loading) return <div className="text-center py-20 text-[var(--ink-muted)]">Loading…</div>;
  if (!user) {
    return (
      <div className="max-w-xl mx-auto px-6 py-20 text-center">
        <h1 className="font-serif text-3xl text-[var(--teak)]">Admin only</h1>
        <p className="text-[var(--ink-muted)] mt-3">Sign in with an admin account.</p>
        <button className="btn-primary mt-6" onClick={() => onAuth?.("login")} data-testid="admin-signin-btn">Sign In</button>
      </div>
    );
  }
  if (user.role !== "admin") {
    return (
      <div className="max-w-xl mx-auto px-6 py-20 text-center">
        <h1 className="font-serif text-3xl text-[var(--teak)]">You are not an admin</h1>
        <p className="text-[var(--ink-muted)] mt-3">This page is restricted.</p>
      </div>
    );
  }

  const act = async (id, status, noteText) => {
    try {
      await api.post(`/admin/properties/${id}/verify`, { status, note: noteText });
      toast.success(`Property ${status}`);
      setRejectFor(null);
      setNote("");
      load();
    } catch {
      toast.error("Failed");
    }
  };

  const removeListing = async (id) => {
    try {
      await api.delete(`/properties/${id}`);
      toast.success("Property deleted — removed from the site");
      setDeleteFor(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const toggleSold = async (p) => {
    try {
      const path = p.sold ? "unmark-sold" : "mark-sold";
      await api.post(`/properties/${p.id}/${path}`);
      toast.success(p.sold ? "Listing is live again" : "Marked as Sold");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
      <div className="mb-8 flex items-start justify-between flex-wrap gap-4">
        <div>
          <span className="section-eyebrow">Admin</span>
          <h1 className="font-serif text-4xl text-[var(--teak)] mt-2">Verification Queue</h1>
          <p className="text-[var(--ink-muted)] mt-2">Review photos, location, and phone number before publishing.</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Link
            to="/post-property"
            className="btn-primary"
            data-testid="admin-add-property-btn"
          >
            <Plus className="w-4 h-4" /> Add property
          </Link>
          <div className="flex gap-2 bg-white p-1 rounded-full border border-[var(--line)]" data-testid="admin-tabs">
            {["pending", "approved", "rejected"].map((s) => (
              <button
                key={s}
                onClick={() => setTab(s)}
                data-testid={`admin-tab-${s}`}
                className={`px-4 py-1.5 rounded-full text-sm font-medium capitalize ${tab === s ? "bg-[var(--teak)] text-white" : "text-[var(--ink-muted)]"}`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      </div>

      {list.length === 0 ? (
        <div className="text-center py-20 text-[var(--ink-muted)]">No {tab} listings right now.</div>
      ) : (
        <div className="space-y-5">
          {list.map((p) => (
            <div key={p.id} className="p-4 lg:p-6 rounded-2xl bg-white border border-[var(--line)] grid lg:grid-cols-12 gap-4" data-testid={`admin-prop-${p.id}`}>
              <div className="lg:col-span-3">
                <div className="aspect-[4/3] rounded-xl overflow-hidden bg-[var(--surface)]">
                  {p.images?.[0] ? <img src={p.images[0]} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[var(--ink-muted)] text-xs">No photo</div>}
                </div>
                <div className="grid grid-cols-3 gap-1 mt-2">
                  {(p.images || []).slice(1, 4).map((im, i) => (
                    <div key={i} className="aspect-square rounded overflow-hidden bg-[var(--surface)]">
                      <img src={im} alt="" className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
              </div>
              <div className="lg:col-span-6">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className="chip">{p.listing_type === "rent" ? "For Rent" : "For Sale"}</span>
                  <span className="chip">{p.property_type}</span>
                  <span className="chip">{p.bhk} BHK · {p.area_sqft} sq.ft</span>
                </div>
                <h3 className="font-serif text-xl text-[var(--teak)]">{p.title}</h3>
                <p className="text-sm text-[var(--ink-muted)] mt-2 line-clamp-3">{p.description}</p>
                <div className="text-xs text-[var(--ink-muted)] mt-3">
                  📍 {p.address} · {p.locality} (lat {p.lat}, lng {p.lng})
                </div>
                <div className="text-xs text-[var(--ink-muted)]">
                  Posted by <strong>{p.owner_name}</strong> ({p.owner_email}) · phone {p.agent_phone || "—"}
                </div>
                <Link to={`/property/${p.id}`} className="text-xs text-[var(--primary)] mt-2 inline-block" data-testid={`admin-view-${p.id}`}>Open public view →</Link>
              </div>
              <div className="lg:col-span-3 flex flex-col gap-2 justify-center">
                <div className="font-mono text-lg text-[var(--primary)] font-bold">{formatINR(p.price, p.listing_type)}</div>
                {tab === "pending" && (
                  <>
                    <button onClick={() => act(p.id, "approved", "Verified by admin")} className="btn-primary justify-center" data-testid={`approve-${p.id}`}>
                      <ShieldCheck className="w-4 h-4" /> Approve
                    </button>
                    <button onClick={() => setRejectFor(p.id)} className="btn-ghost justify-center" data-testid={`reject-${p.id}`}>
                      <X className="w-4 h-4" /> Reject
                    </button>
                    <button
                      onClick={() => setDeleteFor(p.id)}
                      className="btn-ghost justify-center text-red-600 border-red-200 hover:bg-red-50"
                      data-testid={`admin-delete-${p.id}`}
                    >
                      <Trash2 className="w-4 h-4" /> Delete
                    </button>
                  </>
                )}
                {tab !== "pending" && (
                  <>
                    <div className="text-xs text-[var(--ink-muted)]">
                      <Clock className="w-3.5 h-3.5 inline" /> {p.verified_at ? new Date(p.verified_at).toLocaleDateString() : "—"}
                    </div>
                    {tab === "approved" && (
                      <button
                        onClick={() => toggleSold(p)}
                        className={`btn-ghost justify-center ${p.sold ? "text-emerald-700 border-emerald-200" : "text-red-700 border-red-200"}`}
                        data-testid={`admin-sold-toggle-${p.id}`}
                      >
                        {p.sold ? (<><RefreshCw className="w-4 h-4" /> Unmark Sold</>) : (<><Tag className="w-4 h-4" /> Mark Sold</>)}
                      </button>
                    )}
                    <button
                      onClick={() => setDeleteFor(p.id)}
                      className="btn-ghost justify-center text-red-600 border-red-200 hover:bg-red-50 mt-1"
                      data-testid={`admin-delete-${p.id}`}
                    >
                      <Trash2 className="w-4 h-4" /> Delete listing
                    </button>
                  </>
                )}
                {p.verification_note && (
                  <div className="text-xs text-[var(--ink-muted)] italic mt-1">&ldquo;{p.verification_note}&rdquo;</div>
                )}
              </div>
              {rejectFor === p.id && (
                <div className="lg:col-span-12 p-4 bg-[var(--surface)] rounded-xl mt-2">
                  <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Rejection reason (shown to owner)…" data-testid={`reject-reason-${p.id}`} />
                  <div className="flex gap-2 mt-2 justify-end">
                    <button className="btn-ghost" onClick={() => { setRejectFor(null); setNote(""); }}>Cancel</button>
                    <button className="btn-primary" onClick={() => act(p.id, "rejected", note || "Does not meet verification standards")} data-testid={`confirm-reject-${p.id}`}>Confirm Reject</button>
                  </div>
                </div>
              )}
              {deleteFor === p.id && (
                <div
                  className="lg:col-span-12 p-4 bg-red-50 border border-red-200 rounded-xl mt-2"
                  data-testid={`delete-confirm-${p.id}`}
                >
                  <div className="text-sm text-red-900">
                    Permanently delete <b>{p.title}</b>? This also hides it from everyone and cannot be undone.
                  </div>
                  <div className="flex gap-2 mt-3 justify-end">
                    <button className="btn-ghost" onClick={() => setDeleteFor(null)}>Cancel</button>
                    <button
                      onClick={() => removeListing(p.id)}
                      className="btn-primary bg-red-600 hover:bg-red-700"
                      data-testid={`confirm-delete-${p.id}`}
                    >
                      <Trash2 className="w-4 h-4" /> Yes, delete permanently
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
